import { useEffect, useState } from "react";
import { DATA_CHANGED_EVENT, request } from "./api";
import type { Attribution } from "../types";

// The API caps one lookup at 500 ids.
const CHUNK = 500;

// Who created and last changed each of the given records. Lists load their
// rows first and ask for this separately, so a failure here only leaves the
// "edited by" cell empty; the list itself is unaffected.
export function useAttribution(entity: string, ids: string[]): Record<string, Attribution> {
  const [found, setFound] = useState<Record<string, Attribution>>({});
  const [reload, setReload] = useState(0);
  const key = [...new Set(ids)].sort().join(",");

  // An edit made on this page must show up without a refresh.
  useEffect(() => {
    const bump = () => setReload((count) => count + 1);
    window.addEventListener(DATA_CHANGED_EVENT, bump);
    return () => window.removeEventListener(DATA_CHANGED_EVENT, bump);
  }, []);

  useEffect(() => {
    if (!key) return;
    const wanted = key.split(",");
    let cancelled = false;
    const chunks: string[][] = [];
    for (let start = 0; start < wanted.length; start += CHUNK) {
      chunks.push(wanted.slice(start, start + CHUNK));
    }
    Promise.all(
      chunks.map((chunk) =>
        request<Record<string, Attribution>>(
          `/api/admin/attribution/${entity}?ids=${chunk.join(",")}`
        )
      )
    )
      .then((parts) => {
        if (!cancelled) setFound(Object.assign({}, ...parts));
      })
      .catch(() => {
        if (!cancelled) setFound({});
      });
    return () => {
      cancelled = true;
    };
  }, [entity, key, reload]);

  return found;
}
